import { getAccessToken, logout } from './auth';

/**
 * Rücksendungen der Servicepartner.
 *
 * Dieselbe Tabelle und Nummernreihe wie die Lieferscheine hinaus (`RL-…`), nur
 * die andere Richtung. Angenommen wird geräteweise; ein Teilabschluss ist
 * gewollt, damit ein fehlendes Gerät auffällt.
 *
 * Eigener `request`-Helfer wie in den Schwesterdateien — im Haus hat jeder
 * Service seinen, statt einer gemeinsamen Abstraktion.
 */

export interface ReturnSlipDevice {
  stock_device_id:    number;
  stock_number:       string;
  /** Hersteller, Gerät, Farbe und Speicher in einer Zeile — wie im Versanddialog. */
  description:        string;
  imei_sn:            string | null;
  status_id:          number;
  /** null = noch nicht angenommen. */
  received_timestamp: string | null;
  /** Ergebnis beim Partner (jüngster Fall beim Absender) und Grading bei „repariert". */
  outcome:            'repaired' | 'not_repaired' | null;
  grading:            string | null;
}

export interface ReturnSlip {
  id:                 number;
  slip_number:        string;
  creation_timestamp: string;
  servicepartner:     { id: number; description: string };
  total_count:        number;
  received_count:     number;
  devices:            ReturnSlipDevice[];
}

export interface ReceiveResult {
  received:         string[];
  received_count:   number;
  still_open:       string[];
  still_open_count: number;
  devices:          ReturnSlipDevice[];
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  items?: T[];
  item?: T;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code: string) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api/return-slips`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T> & Record<string, unknown>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T> & Record<string, unknown>;
  try {
    body = (await resp.json()) as ApiEnvelope<T> & Record<string, unknown>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result);
  }

  return body;
}

/** Nur Rücksendungen mit offenen Positionen — erledigte gehören nicht in die Arbeitsliste. */
export async function listOpenReturnSlips(): Promise<ReturnSlip[]> {
  const body = await request<ReturnSlip>(BASE, { method: 'GET' });
  return body.items ?? [];
}

/** Abgescannte Geräte annehmen. Was fehlt, bleibt offen und wird so beantwortet. */
export async function receiveReturnSlip(slipId: number, stockDeviceIds: number[]): Promise<ReceiveResult> {
  const body = await request<never>(`${BASE}/${slipId}/receive`, {
    method: 'POST',
    body:   JSON.stringify({ stock_device_ids: stockDeviceIds }),
  });
  return {
    received:         (body.received as string[]) ?? [],
    received_count:   (body.received_count as number) ?? 0,
    still_open:       (body.still_open as string[]) ?? [],
    still_open_count: (body.still_open_count as number) ?? 0,
    devices:          (body.devices as ReturnSlipDevice[]) ?? [],
  };
}
